"use client";

import React from "react";
import Button from "@/components/ui/button/Button";
import DatePicker from "@/components/form/date-picker";
import FileInput from "@/components/form/input/FileInput";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import Checkbox from "@/components/form/input/Checkbox";
import { Modal } from "@/components/ui/modal/index";
import { handleApiError } from "@/lib/api";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useRouter } from "next/navigation";

const moneyStr = z
  .string()
  .min(1, "Required")
  .refine((s) => Number.isFinite(Number(String(s).replace(/,/g, ""))), "Invalid amount");

const createSchema = z.object({
  owner_name: z.string().min(1, "Owner required"),
  registration_no: z.string().min(1, "Registration no. required"),
  asset_type: z.string().min(1, "Asset type required"),
  total_loan_amount: moneyStr,
  loan_end_date: z.string().min(1, "Loan end required"),
  first_emi_date: z.string().min(1, "First EMI date required"),
  bank_name: z.string().min(1, "Bank name required"),
});

const editSchema = z.object({
  first_emi_date: z.string().min(1, "First EMI date required"),
  emi_auto_sync_enabled: z.boolean(),
  registration_no: z.string().min(1, "Registration no. required"),
  capacity_cum: z.string().optional(),
});

const INP =
  "!h-9 !min-h-0 !rounded-md !px-2.5 !py-1.5 !text-sm !shadow-none focus:!ring-2";
const DATE_INP =
  "!h-9 !min-h-0 !rounded-md !px-2.5 !py-1.5 !pr-9 !text-sm !shadow-none focus:!ring-2";
const LBL = "mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400";

const VEHICLE_DOC_FIELDS = [
  ["doc_rc_book", "RC book"],
  ["doc_tax", "Tax"],
  ["doc_permit", "Permit"],
  ["doc_loan_schedule", "Loan schedule"],
  ["doc_puc", "PUC"],
  ["doc_noc", "NOC"],
];

function fmtMoney(v) {
  if (v == null || v === "") return "—";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(v) {
  if (v == null || v === "") return "—";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function fmtDateTime(v) {
  if (v == null || v === "") return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(v);
  }
}

function fmtYesNo(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  return "—";
}

function fmtVehicle(v) {
  if (v == null) return "—";
  if (typeof v === "object") {
    const reg = v.registration_no != null ? String(v.registration_no).trim() : "";
    const id = v.id != null ? v.id : null;
    if (reg && id != null) return `${reg} (vehicle #${id})`;
    if (reg) return reg;
    if (id != null) return `Vehicle #${id}`;
  }
  return String(v);
}

function DetailItem({ label, value }) {
  return (
    <div className="min-w-0">
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {label}
      </span>
      <p className="mt-0.5 break-words text-sm font-medium text-white">{value ?? "—"}</p>
    </div>
  );
}

function toMoneyPayload(s) {
  const n = Number(String(s).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(s ?? "").trim();
  return n.toFixed(2);
}

function buildCreatePayload(data) {
  return {
    owner_name: data.owner_name.trim(),
    registration_no: data.registration_no.trim(),
    asset_type: data.asset_type.trim(),
    total_loan_amount: toMoneyPayload(data.total_loan_amount),
    loan_end_date: data.loan_end_date.trim().slice(0, 10),
    first_emi_date: data.first_emi_date.trim().slice(0, 10),
    bank_name: data.bank_name.trim(),
  };
}

const createDefaults = {
  owner_name: "",
  registration_no: "",
  asset_type: "",
  total_loan_amount: "",
  loan_end_date: "",
  first_emi_date: "",
  bank_name: "",
};

const editDefaults = {
  first_emi_date: "",
  emi_auto_sync_enabled: false,
  registration_no: "",
  capacity_cum: "",
};

export default function VehicleFinanceForm({ recordId = null }) {
  const router = useRouter();
  const qc = useQueryClient();
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const isEdit = recordId != null;

  const [vehicleFiles, setVehicleFiles] = React.useState(() =>
    Object.fromEntries(VEHICLE_DOC_FIELDS.map(([k]) => [k, null])),
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(isEdit ? editSchema : createSchema),
    defaultValues: isEdit ? { ...editDefaults } : { ...createDefaults },
  });

  const hydratedRef = React.useRef(null);

  const { data: detail, isFetching: loadingDetail } = useQuery({
    queryKey: ["vehicleFinanceRecord", recordId],
    queryFn: async () => (await rmcClient.getVehicleFinanceRecord(recordId)).data,
    enabled: recordId != null,
  });

  const vehicleId = detail?.vehicle_id ?? detail?.vehicle?.id ?? null;

  const { data: vehicleRow, isFetching: loadingVehicle } = useQuery({
    queryKey: ["vehicle", vehicleId],
    queryFn: async () => (await rmcClient.getVehicle(vehicleId)).data,
    enabled: recordId != null && vehicleId != null,
  });

  React.useEffect(() => {
    if (recordId == null) hydratedRef.current = null;
  }, [recordId]);

  React.useEffect(() => {
    if (recordId == null || !detail) return;
    const rid = detail.id ?? detail.pk;
    if (Number(rid) !== Number(recordId)) return;
    if (hydratedRef.current === Number(rid)) return;
    if (vehicleId != null && loadingVehicle) return;

    hydratedRef.current = Number(rid);
    const d = detail;
    const str = (v) => (v != null && v !== "" ? String(v) : "");
    const reg = str(vehicleRow?.registration_no ?? d.registration_no);
    reset({
      first_emi_date: str(d.first_emi_date).slice(0, 10),
      emi_auto_sync_enabled: d.emi_auto_sync_enabled === true,
      registration_no: reg,
      capacity_cum:
        vehicleRow?.capacity_cum != null && vehicleRow.capacity_cum !== ""
          ? String(vehicleRow.capacity_cum)
          : "",
    });
    setVehicleFiles(Object.fromEntries(VEHICLE_DOC_FIELDS.map(([k]) => [k, null])));
  }, [recordId, detail, reset, vehicleId, vehicleRow, loadingVehicle]);

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      if (!isEdit) {
        return rmcClient.createVehicleFinanceRecord(payload);
      }
      await rmcClient.updateVehicleFinanceRecord(recordId, payload.finance);
      if (payload.vehicleId != null) {
        const v = payload.vehicle;
        const hasFile = VEHICLE_DOC_FIELDS.some(
          ([k]) => v[k] instanceof File && v[k]?.size > 0,
        );
        const hasCapacity = String(v.capacity_cum ?? "").trim() !== "";
        if (hasFile || hasCapacity || String(v.registration_no ?? "").trim() !== "") {
          await rmcClient.updateVehicle(payload.vehicleId, {
            ...v,
            _vehicleMultipart: true,
          });
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vehicleFinanceRecords"] });
      qc.invalidateQueries({ queryKey: ["vehicles"] });
      qc.invalidateQueries({ queryKey: ["vehicle"] });
      toast.success(isEdit ? "Saved" : "Finance record created");
      router.push("/transport/vehicle-finance");
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => rmcClient.deleteVehicleFinanceRecord(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vehicleFinanceRecords"] });
      toast.success("Record deleted");
      setDeleteOpen(false);
      router.push("/transport/vehicle-finance");
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  const disabledSave =
    saveMutation.isPending || (isEdit && (loadingDetail || (vehicleId != null && loadingVehicle)));

  const onSave = (data) => {
    if (!isEdit) {
      saveMutation.mutate(buildCreatePayload(data));
      return;
    }
    const finance = {
      first_emi_date: data.first_emi_date.trim().slice(0, 10),
      emi_auto_sync_enabled: Boolean(data.emi_auto_sync_enabled),
    };
    const vehicle = {
      registration_no: data.registration_no.trim(),
      capacity_cum: data.capacity_cum ?? "",
      ...vehicleFiles,
    };
    saveMutation.mutate({
      finance,
      vehicleId,
      vehicle,
    });
  };

  const str = (v) => (v != null && v !== "" ? String(v) : "");

  return (
    <div className="max-w-full min-w-0 overflow-x-hidden rounded-lg border border-gray-800/90 bg-gray-950/25 p-3 sm:p-4">
      <div className="mb-4 border-b border-gray-800/80 pb-3">
        <h2 className="text-base font-semibold tracking-tight text-white">
          {isEdit ? "Edit vehicle finance" : "New vehicle finance"}
        </h2>
      </div>

      <div className="space-y-4">
        {!isEdit && (
          <>
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Loan (create)
              </p>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <Label className={LBL}>Owner name</Label>
                  <Controller
                    name="owner_name"
                    control={control}
                    render={({ field }) => (
                      <Input className={INP} {...field} value={field.value || ""} />
                    )}
                  />
                  {errors.owner_name && (
                    <p className="mt-0.5 text-[11px] text-red-400">{errors.owner_name.message}</p>
                  )}
                </div>
                <div>
                  <Label className={LBL}>Registration no.</Label>
                  <Controller
                    name="registration_no"
                    control={control}
                    render={({ field }) => (
                      <Input className={INP} {...field} value={field.value || ""} />
                    )}
                  />
                  {errors.registration_no && (
                    <p className="mt-0.5 text-[11px] text-red-400">
                      {errors.registration_no.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label className={LBL}>Bank name</Label>
                  <Controller
                    name="bank_name"
                    control={control}
                    render={({ field }) => (
                      <Input className={INP} {...field} value={field.value || ""} />
                    )}
                  />
                  {errors.bank_name && (
                    <p className="mt-0.5 text-[11px] text-red-400">{errors.bank_name.message}</p>
                  )}
                </div>
                <div>
                  <Label className={LBL}>Asset type</Label>
                  <Controller
                    name="asset_type"
                    control={control}
                    render={({ field }) => (
                      <Input
                        className={INP}
                        {...field}
                        value={field.value || ""}
                        placeholder="e.g. Truck"
                      />
                    )}
                  />
                  {errors.asset_type && (
                    <p className="mt-0.5 text-[11px] text-red-400">{errors.asset_type.message}</p>
                  )}
                </div>
                <div>
                  <Label className={LBL}>Total loan amount</Label>
                  <Controller
                    name="total_loan_amount"
                    control={control}
                    render={({ field }) => (
                      <Input className={INP} {...field} value={field.value || ""} inputMode="decimal" />
                    )}
                  />
                  {errors.total_loan_amount && (
                    <p className="mt-0.5 text-[11px] text-red-400">
                      {errors.total_loan_amount.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label className={LBL}>Loan end</Label>
                  <Controller
                    name="loan_end_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        id="vf-loan-end"
                        defaultDate={field.value}
                        inputClassName={DATE_INP}
                        onChange={(_, dateStr) => field.onChange(dateStr || "")}
                      />
                    )}
                  />
                  {errors.loan_end_date && (
                    <p className="mt-0.5 text-[11px] text-red-400">{errors.loan_end_date.message}</p>
                  )}
                </div>
                <div>
                  <Label className={LBL}>First EMI date</Label>
                  <Controller
                    name="first_emi_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        id="vf-first-emi-create"
                        defaultDate={field.value}
                        inputClassName={DATE_INP}
                        onChange={(_, dateStr) => field.onChange(dateStr || "")}
                      />
                    )}
                  />
                  {errors.first_emi_date && (
                    <p className="mt-0.5 text-[11px] text-red-400">{errors.first_emi_date.message}</p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {isEdit && loadingDetail && !detail && (
          <p className="text-sm text-gray-400">Loading record…</p>
        )}

        {isEdit && detail && (
          <>
            <div className="space-y-4">
              <div className="rounded-md border border-gray-800/80 bg-gray-950/40 p-3">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Record
                </p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <DetailItem label="ID" value={detail.id != null ? String(detail.id) : "—"} />
                  <DetailItem label="Active" value={fmtYesNo(detail.is_active)} />
                  <DetailItem label="Created" value={fmtDateTime(detail.created_at)} />
                  <DetailItem label="Updated" value={fmtDateTime(detail.updated_at)} />
                </div>
              </div>

              <div className="rounded-md border border-gray-800/80 bg-gray-950/40 p-3">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Party & vehicle
                </p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <DetailItem label="Owner name" value={str(detail.owner_name)} />
                  <DetailItem label="Asset type" value={str(detail.asset_type)} />
                  <DetailItem label="Registration no." value={str(detail.registration_no)} />
                  <DetailItem label="Bank" value={str(detail.bank_name)} />
                  <DetailItem
                    label="Department code"
                    value={detail.department_code != null ? String(detail.department_code) : "—"}
                  />
                  <DetailItem
                    label="Internal vehicle code"
                    value={
                      detail.internal_vehicle_code != null
                        ? String(detail.internal_vehicle_code)
                        : "—"
                    }
                  />
                  <DetailItem label="Linked vehicle" value={fmtVehicle(detail.vehicle)} />
                </div>
              </div>

              <div className="rounded-md border border-gray-800/80 bg-gray-950/40 p-3">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Loan & EMI
                </p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <DetailItem label="Total loan amount" value={fmtMoney(detail.total_loan_amount)} />
                  <DetailItem label="EMI amount" value={fmtMoney(detail.emi_amount)} />
                  <DetailItem
                    label="Total EMI count"
                    value={detail.total_emi_count != null ? String(detail.total_emi_count) : "—"}
                  />
                  <DetailItem label="Paid amount" value={fmtMoney(detail.paid_amount)} />
                  <DetailItem
                    label="Closing loan amount"
                    value={fmtMoney(detail.closing_loan_amount)}
                  />
                  <DetailItem
                    label="EMI installment no."
                    value={
                      detail.emi_installment_number != null
                        ? String(detail.emi_installment_number)
                        : "—"
                    }
                  />
                  <DetailItem label="Loan start" value={fmtDate(detail.loan_start_date)} />
                  <DetailItem label="Loan end" value={fmtDate(detail.loan_end_date)} />
                  <DetailItem label="First EMI date" value={fmtDate(detail.first_emi_date)} />
                  <DetailItem
                    label="EMI auto sync"
                    value={fmtYesNo(detail.emi_auto_sync_enabled)}
                  />
                  <DetailItem label="Next EMI due" value={fmtDate(detail.next_emi_due_date)} />
                  <DetailItem
                    label="Installments past due"
                    value={
                      detail.installments_past_due != null
                        ? String(detail.installments_past_due)
                        : "—"
                    }
                  />
                </div>
              </div>

              <div className="rounded-md border border-gray-800/80 bg-gray-950/40 p-3">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Compliance (expiry dates)
                </p>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                  <DetailItem label="Insurance" value={fmtDate(detail.insurance_expires_on)} />
                  <DetailItem label="PUC" value={fmtDate(detail.puc_expires_on)} />
                  <DetailItem label="Fitness" value={fmtDate(detail.fitness_expires_on)} />
                  <DetailItem label="Tax" value={fmtDate(detail.tax_expires_on)} />
                  <DetailItem label="Permit" value={fmtDate(detail.permit_expires_on)} />
                </div>
              </div>

              <div className="rounded-md border border-gray-800/80 bg-gray-950/40 p-3">
                <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Remark
                </p>
                <p className="whitespace-pre-wrap break-words text-sm font-medium text-white">
                  {str(detail.remark) ? str(detail.remark) : "—"}
                </p>
              </div>

              {!vehicleId && (
                <p className="text-[11px] text-amber-400/90">
                  No linked vehicle yet — upload documents after a vehicle is linked to this
                  registration.
                </p>
              )}
            </div>

            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                EMI sync (edit)
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <Label className={LBL}>First EMI date</Label>
                  <Controller
                    name="first_emi_date"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        id="vf-first-emi-edit"
                        defaultDate={field.value}
                        inputClassName={DATE_INP}
                        onChange={(_, dateStr) => field.onChange(dateStr || "")}
                      />
                    )}
                  />
                  {errors.first_emi_date && (
                    <p className="mt-0.5 text-[11px] text-red-400">{errors.first_emi_date.message}</p>
                  )}
                </div>
                <div className="flex items-end pb-1">
                  <Controller
                    name="emi_auto_sync_enabled"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="vf-emi-auto-sync"
                        label="EMI auto sync"
                        checked={Boolean(field.value)}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Vehicle & documents
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <Label className={LBL}>Registration no.</Label>
                  <Controller
                    name="registration_no"
                    control={control}
                    render={({ field }) => (
                      <Input
                        className={INP}
                        {...field}
                        value={field.value || ""}
                        disabled={!vehicleId}
                      />
                    )}
                  />
                  {errors.registration_no && (
                    <p className="mt-0.5 text-[11px] text-red-400">
                      {errors.registration_no.message}
                    </p>
                  )}
                </div>
                <div>
                  <Label className={LBL}>Capacity (cum)</Label>
                  <Controller
                    name="capacity_cum"
                    control={control}
                    render={({ field }) => (
                      <Input
                        className={INP}
                        {...field}
                        value={field.value || ""}
                        inputMode="decimal"
                        disabled={!vehicleId}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {VEHICLE_DOC_FIELDS.map(([key, label]) => (
                  <div key={key}>
                    <Label className={LBL}>{label}</Label>
                    <FileInput
                      name={key}
                      accept=".pdf,image/*"
                      disabled={!vehicleId}
                      onChange={(e) => {
                        const f = e.target.files?.[0] ?? null;
                        setVehicleFiles((prev) => ({ ...prev, [key]: f }));
                      }}
                    />
                    {vehicleFiles[key] instanceof File && (
                      <p className="mt-0.5 text-[10px] text-gray-500">
                        Selected: {vehicleFiles[key].name}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-center gap-2 border-t border-gray-800/80 pt-4">
        <Button
          type="button"
          size="sm"
          disabled={disabledSave}
          onClick={handleSubmit(onSave)}
        >
          {saveMutation.isPending ? "Saving…" : isEdit ? "Save" : "Create"}
        </Button>
        {isEdit && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-red-500/40 text-red-400"
            disabled={deleteMutation.isPending}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={saveMutation.isPending}
          onClick={() => router.push("/transport/vehicle-finance")}
        >
          Close
        </Button>
      </div>

      <Modal
        isOpen={deleteOpen}
        onClose={() => {
          if (!deleteMutation.isPending) setDeleteOpen(false);
        }}
        className="max-w-md w-full"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Delete finance record?</h3>
          <p className="text-sm text-gray-400">This cannot be undone.</p>
          <div className="flex justify-end gap-2 border-t border-gray-800 pt-4">
            <Button type="button" variant="outline" size="sm" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
              onClick={() => recordId != null && deleteMutation.mutate(recordId)}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
